/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

const surface   = '#fafaf8';
const surfaceHi = '#f5f0e8';
const border    = '#d4c8b4';
const gold      = '#b8956a';
const text      = '#0d3d5c';
const textSec   = '#12526e';
const textMut   = '#4a6070';
const green     = '#1A7A4A';
const red       = '#CC2200';
const fHead = "'Fraunces', Georgia, serif";
const fBody = "'EB Garamond', Georgia, serif";

type Tab = 'curriculum' | 'markets' | 'properties' | 'journal' | 'glossary' | 'reading';

interface CurriculumItem {
  key: string;
  order: number;
  title: string;
  url: string;
  source: string;
  est_time: string;
  why: string;
  completed: boolean;
  completed_at: string | null;
}

interface JournalEntry {
  id: number;
  content: string;
  tags: string | null;
  created_at: string | null;
}

interface GlossaryTerm {
  term: string;
  definition: string;
  formula?: string;
  source?: string;
}

interface LibrarySection {
  section: string;
  items: { title: string; url: string; source: string; summary: string }[];
}

interface MarketIndicator {
  key: string;
  label: string;
  unit_hint: string;
  fmt: 'int' | 'int_compact' | 'int_signed' | 'money' | 'money_per_month' | 'pct' | 'decimal' | string;
  source_label: string;
}

interface MarketMetric {
  value: number | null;
  year: number | null;
  prev_value: number | null;
  prev_year: number | null;
  unit: string | null;
  source: string | null;
  vintage: string | null;
  yoy_pct: number | null;
}

interface MarketEntry {
  name: string;
  state: string;
  cbsa: string;
  metrics: Record<string, MarketMetric | null>;
}

interface SheetMeta {
  id: number;
  label: string;
  csv_url: string;
  created_at: string | null;
  last_fetched_at: string | null;
  last_status: string | null;
  last_error: string | null;
  row_count: number;
  orientation?: 'wide' | 'tall' | string;
}

interface PropertyRow {
  _row_index?: number;
  _raw?: Record<string, string>;
  name?: string | string[] | null;
  building_type?: string | string[] | null;
  year_built?: number | number[] | null;
  location?: string | string[] | null;
  apt_types_offered?: string | string[] | null;
  pet_policy?: string | string[] | null;
  staff_management?: string | string[] | null;
  rent_ranges?: string | string[] | null;
  sqft_ranges?: string | string[] | null;
  studio_monthly?: number | number[] | null;
  studio_sqft?: number | number[] | null;
  '1b1b_monthly'?: number | number[] | null;
  '1b1b_sqft'?: number | number[] | null;
  '2b2b_monthly'?: number | number[] | null;
  '2b2b_sqft'?: number | number[] | null;
  [k: string]: unknown;
}

interface RankedRow { name: string; row_index: number; value: number; }
interface Ranking {
  label: string;
  metric_unit: string;
  lower_is_better: boolean;
  ranked: RankedRow[];
}

interface Peer {
  name: string;
  row_index: number;
  distance: number;
  why: string | null;
}
interface SimilarityPayload {
  names: Record<string, string>;
  peers: Record<string, Peer[]>;
}

interface PersonaDef {
  id: 'budget' | 'value' | 'balanced' | 'premium' | 'luxury' | string;
  label: string;
  tagline: string;
  desc: string;
  primary_label: string;
  primary_field: string;
  primary_dir: 'asc' | 'desc' | string;
}
interface PersonaFact { label: string; value: string; }
interface PersonaRankedRow {
  name: string;
  row_index: number;
  primary_value: number | null;
  primary_display: string;
  facts: PersonaFact[];
}
interface NicheLead {
  dimension: string;
  rank: number;
  score: number;
  label: string;
  detail: string;
}
interface NicheEntry {
  name: string;
  row_index: number;
  leading: NicheLead[];
  scores: Record<string, number | null>;
  price: number | null;
  income_min: number | null;
  newness: number | null;
  amenity: number | null;
}
interface ComparisonPayload {
  personas: PersonaDef[];
  rankings: Record<string, PersonaRankedRow[]>;
  niches:   NicheEntry[];
}

interface ColumnMapEntry { header: string; field: string | null; }
interface SampleFieldEntry {
  field: string;
  parsed: unknown;
  source_cells: string[];
}
interface SampleRow {
  name: string;
  row_index: number;
  fields: SampleFieldEntry[];
}
interface DiagIssue {
  property: string;
  field: string;
  raw_cell: string;
  reason: string;
  fix: string;
}
interface SheetDiagnostics {
  headers: string[];
  column_map: ColumnMapEntry[];
  unmatched: string[];
  matched_fields: Record<string, number>;
  issues?: DiagIssue[];
  sample_rows?: SampleRow[];
}

function fmtRanked(v: number, unit: string): string {
  if (unit.includes('$/sqft')) return `$${v.toFixed(2)}/sqft`;
  if (unit.includes('$/mo'))   return `$${Math.round(v).toLocaleString()}/mo`;
  if (unit.includes('$/yr'))   return `$${Math.round(v).toLocaleString()}/yr`;
  if (unit === 'sqft')         return `${Math.round(v).toLocaleString()} sqft`;
  if (unit === 'year')         return `${Math.round(v)}`;
  return v.toLocaleString();
}

function firstScalar(v: unknown): string | number | null {
  if (v === null || v === undefined) return null;
  if (Array.isArray(v)) return v.length ? (v[0] as string | number) : null;
  return v as string | number;
}

// Look up a value from the parsed canonical field, falling back to a
// case-insensitive search of the raw header → cell map. The fallback is
// strict: exact match first, then word-boundary match. NEVER substring,
// because "building" would match "building amenities" and pollute the
// Property column with amenity lists.
function pickCell(p: PropertyRow, canonicalKey: string, rawHeaderHints: string[]): string {
  const canon = firstScalar(p[canonicalKey] as unknown);
  if (canon !== null && canon !== undefined && canon !== '') return String(canon);
  const raw = (p._raw || {}) as Record<string, string>;
  const lowerKeys = Object.keys(raw).map(k => [k, k.toLowerCase().trim()] as const);
  // 1. Exact case-insensitive match
  for (const hint of rawHeaderHints) {
    const h = hint.toLowerCase().trim();
    const exact = lowerKeys.find(([, lk]) => lk === h);
    if (exact && raw[exact[0]]) return raw[exact[0]];
  }
  // 2. Word-boundary match — hint must appear as a complete word
  for (const hint of rawHeaderHints) {
    const h = hint.toLowerCase().trim();
    const escaped = h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`\\b${escaped}\\b`);
    const wb = lowerKeys.find(([, lk]) => re.test(lk));
    if (wb && raw[wb[0]]) return raw[wb[0]];
  }
  return '—';
}

function avgNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (Array.isArray(v)) {
    const ns = v.filter(x => typeof x === 'number') as number[];
    return ns.length ? ns.reduce((a, b) => a + b, 0) / ns.length : null;
  }
  if (typeof v === 'number') return v;
  return null;
}

function propName(p: PropertyRow): string {
  // Try canonical first
  const canon = firstScalar(p.name as unknown);
  if (canon) return String(canon);
  // Try strict header matches
  const strict = pickCell(p, 'name', ['property','property name','building name','name']);
  if (strict !== '—') return strict;
  // Last resort: first non-blank value in the raw row whose header is
  // blank (this handles tall sheets where the green-coloured property
  // name lived in the first cell of a column with no field label).
  const raw = (p._raw || {}) as Record<string, string>;
  for (const [h, v] of Object.entries(raw)) {
    if (!h.trim() && v && String(v).trim()) return String(v).trim();
  }
  // Or the very first non-blank value
  for (const v of Object.values(raw)) {
    const s = String(v || '').trim();
    if (s && s.length < 60) return s;
  }
  return '(unnamed)';
}

// Compute a delta string like "+$200/mo (+8.7%) vs you" with sign + colour
function deltaCell(self: number | null, peer: number | null,
                   fmtUnit: 'money' | 'sqft' | 'year',
                   lowerIsBetter: boolean): {
  display: string; color: string; raw: string;
} {
  const grey = '#4a6070', greenC = '#1A7A4A', redC = '#CC2200';
  if (peer === null || peer === undefined) {
    return { display: '—', color: grey, raw: '' };
  }
  const peerStr =
    fmtUnit === 'money' ? `$${Math.round(peer).toLocaleString()}` :
    fmtUnit === 'sqft'  ? `${Math.round(peer).toLocaleString()} sqft` :
                          `${Math.round(peer)}`;
  if (self === null || self === undefined) {
    return { display: peerStr, color: '#0d3d5c', raw: peerStr };
  }
  const diff = peer - self;
  if (diff === 0) {
    return { display: `${peerStr} = you`, color: grey, raw: peerStr };
  }
  const pct = self !== 0 ? (diff / self) * 100 : 0;
  const sign = diff > 0 ? '+' : '−';
  const absDiff = Math.abs(diff);
  const diffStr =
    fmtUnit === 'money' ? `${sign}$${Math.round(absDiff).toLocaleString()}` :
    fmtUnit === 'sqft'  ? `${sign}${Math.round(absDiff).toLocaleString()} sqft` :
                          `${sign}${Math.round(absDiff)} yr`;
  // Determine "good" direction for renter
  const isBetter = lowerIsBetter ? diff < 0 : diff > 0;
  const color = isBetter ? greenC : redC;
  return {
    display: `${peerStr}  (${diffStr}, ${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)`,
    color,
    raw: peerStr,
  };
}

function fmtMetric(m: MarketMetric | null, fmt: string): string {
  if (!m || m.value === null || m.value === undefined) return '—';
  const v = Number(m.value);
  if (!Number.isFinite(v)) return '—';
  switch (fmt) {
    case 'int':
      return Math.round(v).toLocaleString();
    case 'int_compact':
      if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
      if (v >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
      return Math.round(v).toLocaleString();
    case 'int_signed': {
      const sign = v > 0 ? '+' : v < 0 ? '−' : '';
      return `${sign}${Math.abs(Math.round(v)).toLocaleString()}`;
    }
    case 'money':
      if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
      if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
      if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}k`;
      return `$${Math.round(v).toLocaleString()}`;
    case 'money_per_month':
      return `$${Math.round(v).toLocaleString()}/mo`;
    case 'pct':
      return `${v.toFixed(1)}%`;
    case 'decimal':
      return v.toFixed(1);
    default:
      return String(v);
  }
}

interface Props {
  token: string;
  open: boolean;
  initialTab?: Tab;
  onClose: () => void;
}

export default function Library({ token, open, initialTab = 'curriculum', onClose }: Props) {
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [tab, setTab] = useState<Tab>(initialTab);

  // Curriculum state
  const [curriculum, setCurriculum] = useState<CurriculumItem[]>([]);
  const [curriculumStats, setCurriculumStats] = useState<{done: number; total: number; progress: number}>({ done: 0, total: 0, progress: 0 });
  const [curriculumLoaded, setCurriculumLoaded] = useState(false);

  const loadCurriculum = useCallback(() => {
    axios.get(`${API}/me/curriculum`, { headers: auth })
      .then(r => {
        setCurriculum(r.data?.items || []);
        setCurriculumStats({
          done: r.data?.done || 0,
          total: r.data?.total || 0,
          progress: r.data?.progress || 0,
        });
      })
      .catch(() => {})
      .finally(() => setCurriculumLoaded(true));
  }, [auth]);

  const toggleCurriculum = async (key: string) => {
    // Optimistic
    setCurriculum(prev => prev.map(it =>
      it.key === key ? { ...it, completed: !it.completed } : it
    ));
    try {
      await axios.post(`${API}/me/curriculum/${key}/toggle`, {}, { headers: auth });
      loadCurriculum();
    } catch {
      loadCurriculum();
    }
  };

  // Journal state
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [draft, setDraft] = useState('');
  const [draftTags, setDraftTags] = useState('');
  const [savingEntry, setSavingEntry] = useState(false);
  const [entriesLoaded, setEntriesLoaded] = useState(false);

  // Glossary state
  const [glossary, setGlossary] = useState<GlossaryTerm[]>([]);
  const [glossaryLoaded, setGlossaryLoaded] = useState(false);
  const [glossaryQ, setGlossaryQ] = useState('');

  // Library state
  const [library, setLibrary] = useState<LibrarySection[]>([]);
  const [libraryLoaded, setLibraryLoaded] = useState(false);

  // Markets state
  const [marketIndicators, setMarketIndicators] = useState<MarketIndicator[]>([]);
  const [markets, setMarkets] = useState<MarketEntry[]>([]);
  const [marketsLoaded, setMarketsLoaded] = useState(false);
  const [marketActive, setMarketActive] = useState<string | null>(null);

  // Properties (Google Sheets) state
  const [sheets, setSheets] = useState<SheetMeta[]>([]);
  const [sheetsLoaded, setSheetsLoaded] = useState(false);
  const [activeSheetId, setActiveSheetId] = useState<number | null>(null);
  const [properties, setProperties] = useState<PropertyRow[]>([]);
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [propsBusy, setPropsBusy] = useState(false);
  const [connectLabel, setConnectLabel] = useState('');
  const [connectUrl, setConnectUrl] = useState('');
  const [connectErr, setConnectErr] = useState<string | null>(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const [propView, setPropView] = useState<'rankings' | 'table' | 'peers' | 'diagnostics'>('rankings');
  const [similarity, setSimilarity] = useState<SimilarityPayload | null>(null);
  const [comparison, setComparison] = useState<ComparisonPayload | null>(null);
  const [activePersona, setActivePersona] = useState<string>('value');
  const [formatHelpOpen, setFormatHelpOpen] = useState(false);
  const [diagnostics, setDiagnostics] = useState<SheetDiagnostics | null>(null);
  const [focalRowIdx, setFocalRowIdx] = useState<number | null>(null);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discoverBusy, setDiscoverBusy] = useState(false);
  const [discoverData, setDiscoverData] = useState<{
    focal:        { label: string | null; lat: number; lng: number };
    radius_miles: number;
    discovered:   number;
    candidates:   Array<{
      name:           string;
      has_name:       boolean;
      address:        string | null;
      lat:            number;
      lng:            number;
      year:           string | null;
      floors:         string | null;
      units:          string | null;
      distance_miles: number;
      row_template:   Array<{ field: string; value: string }>;
      search_urls:    Record<string, string>;
    }>;
    scrape_ready: boolean;
    fill_message: string;
  } | null>(null);
  const [discoverErr, setDiscoverErr] = useState<string | null>(null);
  const [discoverRadius, setDiscoverRadius] = useState<number>(3);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const loadSheets = useCallback(() => {
    axios.get(`${API}/sheets/list`, { headers: auth })
      .then(r => {
        const list: SheetMeta[] = r.data?.sheets || [];
        setSheets(list);
        if (list.length && activeSheetId === null) setActiveSheetId(list[0].id);
      })
      .catch(() => setSheets([]))
      .finally(() => setSheetsLoaded(true));
  }, [auth, activeSheetId]);

  const loadSheetData = useCallback((sid: number) => {
    setPropsBusy(true);
    Promise.all([
      axios.get(`${API}/sheets/${sid}/properties`, { headers: auth }),
      axios.get(`${API}/sheets/${sid}/analysis`,   { headers: auth }),
      axios.get(`${API}/sheets/${sid}/similarity`, { headers: auth }),
      axios.get(`${API}/sheets/${sid}/comparison`, { headers: auth }),
    ])
      .then(([pr, an, sim, cmp]) => {
        setProperties(pr.data?.properties || []);
        setRankings(an.data?.rankings || []);
        setSimilarity({
          names: sim.data?.names || {},
          peers: sim.data?.peers || {},
        });
        setComparison({
          personas: cmp.data?.personas || [],
          rankings: cmp.data?.rankings || {},
          niches:   cmp.data?.niches   || [],
        });
        setDiagnostics(pr.data?.diagnostics || null);
      })
      .catch(() => {
        setProperties([]); setRankings([]); setSimilarity(null);
        setComparison(null); setDiagnostics(null);
      })
      .finally(() => setPropsBusy(false));
  }, [auth]);

  useEffect(() => {
    if (tab === 'properties' && activeSheetId !== null) {
      loadSheetData(activeSheetId);
    }
  }, [tab, activeSheetId, loadSheetData]);

  const connectSheet = async () => {
    const url = connectUrl.trim();
    const label = connectLabel.trim() || 'My Sheet';
    if (!url) { setConnectErr('Paste a Google Sheets published-CSV URL.'); return; }
    setConnectErr(null);
    setConnectBusy(true);
    try {
      const r = await axios.post(`${API}/sheets/connect`,
        { url, label }, { headers: auth });
      const newId = r.data?.sheet_id;
      setConnectUrl(''); setConnectLabel('');
      setSheetsLoaded(false);
      if (newId) setActiveSheetId(newId);
      // Re-list
      const list = (await axios.get(`${API}/sheets/list`, { headers: auth })).data?.sheets || [];
      setSheets(list);
      setSheetsLoaded(true);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
                || (e as Error)?.message || 'Failed to connect sheet';
      setConnectErr(msg);
    } finally {
      setConnectBusy(false);
    }
  };

  const refreshSheet = async (sid: number) => {
    setPropsBusy(true);
    try {
      await axios.post(`${API}/sheets/${sid}/refresh`, {}, { headers: auth });
      loadSheetData(sid);
      loadSheets();
    } catch {
      setPropsBusy(false);
    }
  };

  const runDiscoverNearby = async (sid: number, rowIndex: number) => {
    setDiscoverErr(null);
    setDiscoverBusy(true);
    setDiscoverData(null);
    setDiscoverOpen(true);
    try {
      const r = await axios.post(`${API}/sheets/${sid}/discover-nearby`,
        { row_index: rowIndex, radius_miles: discoverRadius, max_results: 25 },
        { headers: auth });
      setDiscoverData(r.data);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
                || (e as Error)?.message || 'Discovery failed';
      setDiscoverErr(String(msg));
    } finally {
      setDiscoverBusy(false);
    }
  };

  const disconnectSheet = async (sid: number) => {
    try {
      await axios.delete(`${API}/sheets/${sid}`, { headers: auth });
      setSheets(prev => prev.filter(s => s.id !== sid));
      if (activeSheetId === sid) {
        setActiveSheetId(null);
        setProperties([]); setRankings([]);
      }
    } catch { /* swallow */ }
  };

  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  const loadJournal = useCallback(() => {
    axios.get(`${API}/me/journal?limit=200`, { headers: auth })
      .then(r => setEntries(r.data?.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setEntriesLoaded(true));
  }, [auth]);

  useEffect(() => {
    if (!open) return;
    if (tab === 'curriculum' && !curriculumLoaded) loadCurriculum();
    if (tab === 'journal' && !entriesLoaded) loadJournal();
    if (tab === 'glossary' && !glossaryLoaded) {
      axios.get(`${API}/me/glossary`, { headers: auth })
        .then(r => setGlossary(r.data?.glossary || []))
        .catch(() => setGlossary([]))
        .finally(() => setGlossaryLoaded(true));
    }
    if (tab === 'reading' && !libraryLoaded) {
      axios.get(`${API}/me/library`, { headers: auth })
        .then(r => setLibrary(r.data?.library || []))
        .catch(() => setLibrary([]))
        .finally(() => setLibraryLoaded(true));
    }
    if (tab === 'markets' && !marketsLoaded) {
      axios.get(`${API}/me/markets`, { headers: auth })
        .then(r => {
          setMarketIndicators(r.data?.indicators || []);
          const ms: MarketEntry[] = r.data?.markets || [];
          setMarkets(ms);
          if (ms.length && !marketActive) setMarketActive(ms[0].cbsa);
        })
        .catch(() => { setMarketIndicators([]); setMarkets([]); })
        .finally(() => setMarketsLoaded(true));
    }
    if (tab === 'properties' && !sheetsLoaded) {
      loadSheets();
    }
  }, [open, tab, entriesLoaded, glossaryLoaded, libraryLoaded, marketsLoaded, marketActive, sheetsLoaded, loadJournal, loadSheets, auth]);

  const saveEntry = async () => {
    const content = draft.trim();
    if (!content || savingEntry) return;
    setSavingEntry(true);
    try {
      await axios.post(`${API}/me/journal`,
        { content, tags: draftTags.trim() || null },
        { headers: auth });
      setDraft('');
      setDraftTags('');
      loadJournal();
    } catch {
      // swallow — UI shows nothing if save fails; user can retry
    } finally {
      setSavingEntry(false);
    }
  };

  const deleteEntry = async (id: number) => {
    setEntries(prev => prev.filter(e => e.id !== id));
    try { await axios.delete(`${API}/me/journal/${id}`, { headers: auth }); }
    catch { loadJournal(); }
  };

  if (!open) return null;

  const filteredGlossary = glossaryQ.trim()
    ? glossary.filter(g => {
        const q = glossaryQ.toLowerCase();
        return g.term.toLowerCase().includes(q)
            || g.definition.toLowerCase().includes(q);
      })
    : glossary;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(13,61,92,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 24px', overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          width: '100%', maxWidth: 920,
          border: `1px solid ${border}`,
          borderTop: `3px solid ${gold}`,
          boxShadow: '0 8px 32px rgba(13,61,92,0.25)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 28px 12px',
          borderBottom: `1px solid ${border}`,
          display: 'flex', alignItems: 'center', gap: 16,
        }}>
          <div>
            <div style={{
              color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
              letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 4,
            }}>
              Coast · Personal
            </div>
            <h2 style={{
              fontFamily: fHead, color: text, fontSize: 22, fontWeight: 800,
              margin: 0,
            }}>
              Library
            </h2>
          </div>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{
            background: 'none', border: `1px solid ${border}`,
            color: textMut, fontFamily: fBody, fontSize: 11,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            padding: '6px 14px', cursor: 'pointer',
          }}>
            Close
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', borderBottom: `1px solid ${border}`,
          padding: '0 28px',
        }}>
          {(['curriculum', 'markets', 'properties', 'journal', 'glossary', 'reading'] as const).map(t => {
            const active = tab === t;
            const label =
              t === 'curriculum' ? 'Read Order'       :
              t === 'markets'    ? 'Markets'          :
              t === 'properties' ? 'Properties'       :
              t === 'journal'    ? 'Journal'          :
              t === 'glossary'   ? 'Glossary'         :
                                   'All Sources';
            return (
              <button key={t} onClick={() => setTab(t)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '12px 14px',
                color: active ? text : textMut,
                fontFamily: fBody, fontSize: 12, fontWeight: active ? 700 : 500,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                borderBottom: `2px solid ${active ? gold : 'transparent'}`,
                marginBottom: -1,
              }}>
                {label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ padding: '20px 28px 28px', maxHeight: '70vh', overflowY: 'auto' }}>
          {tab === 'curriculum' && (
            <>
              {/* Progress header */}
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 14,
                marginBottom: 16,
              }}>
                <div style={{
                  fontFamily: fHead, color: text, fontSize: 22, fontWeight: 800,
                }}>
                  {curriculumStats.done} <span style={{
                    color: textMut, fontFamily: fBody, fontSize: 14, fontWeight: 500,
                  }}>of {curriculumStats.total} read</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    width: '100%', height: 6, background: surfaceHi,
                    border: `1px solid ${border}`, position: 'relative',
                  }}>
                    <div style={{
                      position: 'absolute', top: 0, left: 0, bottom: 0,
                      width: `${Math.round(curriculumStats.progress * 100)}%`,
                      background: green,
                      transition: 'width 0.25s ease',
                    }} />
                  </div>
                </div>
              </div>

              <p style={{
                fontFamily: fBody, color: textSec, fontSize: 12,
                lineHeight: 1.6, margin: '0 0 18px 0',
              }}>
                Sequenced reading list. Click each title to open the primary source
                in a new tab; tick the checkbox once you've read it. Your progress
                is saved server-side so it follows the login. Total est. time
                across all items: ~3 hours.
              </p>

              {!curriculumLoaded ? (
                <div style={{ color: textMut, fontFamily: fBody, fontSize: 13 }}>Loading…</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {curriculum.map(it => (
                    <div key={it.key} style={{
                      display: 'flex', gap: 14, alignItems: 'flex-start',
                      padding: '14px 16px',
                      border: `1px solid ${border}`,
                      borderLeft: `3px solid ${it.completed ? green : gold}`,
                      background: it.completed ? '#f3f7f4' : surface,
                      opacity: it.completed ? 0.85 : 1,
                    }}>
                      {/* Order badge + checkbox */}
                      <div style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', gap: 8, flexShrink: 0,
                      }}>
                        <div style={{
                          width: 28, height: 28,
                          background: it.completed ? green : 'transparent',
                          color: it.completed ? '#fff' : text,
                          border: `1.5px solid ${it.completed ? green : border}`,
                          borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: fHead, fontSize: 13, fontWeight: 800,
                        }}>
                          {it.order}
                        </div>
                        <button
                          onClick={() => toggleCurriculum(it.key)}
                          title={it.completed ? 'Mark unread' : 'Mark read'}
                          style={{
                            width: 22, height: 22,
                            background: it.completed ? green : '#fff',
                            border: `1.5px solid ${it.completed ? green : border}`,
                            color: '#fff', cursor: 'pointer',
                            fontFamily: fBody, fontSize: 13, fontWeight: 700,
                            padding: 0, lineHeight: 1,
                          }}>
                          {it.completed ? '✓' : ''}
                        </button>
                      </div>

                      {/* Card body */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <a href={it.url} target="_blank" rel="noopener noreferrer"
                          style={{
                            display: 'block',
                            fontFamily: fHead, color: text, fontSize: 15,
                            fontWeight: 800, lineHeight: 1.3,
                            textDecoration: it.completed ? 'line-through' : 'none',
                            marginBottom: 4,
                          }}>
                          {it.title}
                        </a>
                        <div style={{
                          fontFamily: fBody, color: textSec, fontSize: 12,
                          lineHeight: 1.55, marginBottom: 6,
                        }}>
                          {it.why}
                        </div>
                        <div style={{
                          display: 'flex', gap: 14, flexWrap: 'wrap',
                          color: textMut, fontFamily: fBody, fontSize: 10,
                          letterSpacing: '0.06em',
                        }}>
                          <span style={{ color: gold, fontWeight: 700 }}>
                            {it.source}
                          </span>
                          <span>·</span>
                          <span>{it.est_time}</span>
                          {it.completed_at && (
                            <>
                              <span>·</span>
                              <span style={{ color: green }}>
                                Read {new Date(it.completed_at).toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'markets' && (
            <>
              <p style={{
                fontFamily: fBody, color: textSec, fontSize: 13,
                lineHeight: 1.6, margin: '0 0 14px 0',
              }}>
                Per-market macro stack for your focused MSAs. Every value is the
                most-recent observation in Coast's database, with year and primary
                source labelled. Year-over-year deltas are computed against the
                immediately prior annual value when available.
              </p>

              {!marketsLoaded ? (
                <div style={{ color: textMut, fontFamily: fBody, fontSize: 13 }}>Loading…</div>
              ) : markets.length === 0 ? (
                <div style={{
                  color: textMut, fontFamily: fBody, fontSize: 13,
                  fontStyle: 'italic', padding: '24px 0', textAlign: 'center',
                  border: `1px dashed ${border}`,
                }}>
                  No focused markets configured.
                </div>
              ) : (
                <>
                  {/* Metro tab strip */}
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 6,
                    marginBottom: 16,
                  }}>
                    {markets.map(m => {
                      const active = marketActive === m.cbsa;
                      return (
                        <button key={m.cbsa} onClick={() => setMarketActive(m.cbsa)}
                          style={{
                            background: active ? gold : '#fff',
                            color: active ? '#fff' : text,
                            border: `1px solid ${active ? gold : border}`,
                            fontFamily: fBody, fontSize: 11,
                            fontWeight: active ? 700 : 500,
                            letterSpacing: '0.06em',
                            padding: '5px 11px', cursor: 'pointer',
                          }}>
                          {m.name}, {m.state}
                        </button>
                      );
                    })}
                  </div>

                  {(() => {
                    const m = markets.find(x => x.cbsa === marketActive) || markets[0];
                    if (!m) return null;
                    return (
                      <div>
                        <div style={{
                          fontFamily: fHead, color: text, fontSize: 18,
                          fontWeight: 800, marginBottom: 4,
                        }}>
                          {m.name}, {m.state}
                        </div>
                        <div style={{
                          color: textMut, fontFamily: fBody, fontSize: 11,
                          letterSpacing: '0.06em', marginBottom: 14,
                        }}>
                          CBSA {m.cbsa}
                        </div>

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                          gap: 10,
                        }}>
                          {marketIndicators.map(ind => {
                            const metric = m.metrics?.[ind.key] || null;
                            const yoy = metric?.yoy_pct;
                            const yoyColor = yoy === null || yoy === undefined ? textMut
                              : yoy > 0 ? green : yoy < 0 ? red : textMut;
                            return (
                              <div key={ind.key} style={{
                                background: surface,
                                border: `1px solid ${border}`,
                                borderLeft: `3px solid ${metric ? gold : border}`,
                                padding: '10px 12px',
                              }}>
                                <div style={{
                                  color: textMut, fontFamily: fBody, fontSize: 10,
                                  letterSpacing: '0.08em', textTransform: 'uppercase',
                                  marginBottom: 4,
                                }}>
                                  {ind.label}
                                </div>
                                <div style={{
                                  fontFamily: fHead, color: text, fontSize: 20,
                                  fontWeight: 800, lineHeight: 1.1, marginBottom: 4,
                                }}>
                                  {fmtMetric(metric, ind.fmt)}
                                </div>
                                <div style={{
                                  display: 'flex', gap: 8, alignItems: 'baseline',
                                  flexWrap: 'wrap',
                                }}>
                                  {yoy !== null && yoy !== undefined && (
                                    <span style={{
                                      color: yoyColor, fontFamily: fBody, fontSize: 11,
                                      fontWeight: 700,
                                    }}>
                                      {yoy > 0 ? '+' : ''}{yoy.toFixed(1)}% YoY
                                    </span>
                                  )}
                                  {metric?.year && (
                                    <span style={{
                                      color: textMut, fontFamily: fBody, fontSize: 10,
                                    }}>
                                      {metric.year}
                                    </span>
                                  )}
                                </div>
                                <div style={{
                                  color: textMut, fontFamily: fBody, fontSize: 9,
                                  letterSpacing: '0.06em', marginTop: 6,
                                  textTransform: 'uppercase',
                                }}>
                                  {metric?.source || ind.source_label}
                                  {metric?.vintage ? ` · ${metric.vintage}` : ''}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </>
          )}

          {tab === 'properties' && (
            <>
              <p style={{
                fontFamily: fBody, color: textSec, fontSize: 13,
                lineHeight: 1.6, margin: '0 0 10px 0',
              }}>
                Connect a Google Sheet of residential properties — Coast accepts
                both layouts: one property per row OR one property per column
                (with field labels down the left). The orientation is auto-detected.
                To connect: in your sheet, File → Share → Publish to web → CSV → copy
                the link, then paste it below.
              </p>

              {/* Format Guide — collapsible reference for the user */}
              <div style={{
                border: `1px solid ${border}`,
                borderLeft: `3px solid ${gold}`,
                marginBottom: 14,
                background: surface,
              }}>
                <button
                  onClick={() => setFormatHelpOpen(o => !o)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    gap: 12, padding: '10px 14px',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    textAlign: 'left',
                  }}>
                  <span style={{
                    color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.22em', textTransform: 'uppercase',
                  }}>
                    Format Guide
                  </span>
                  <span style={{
                    color: textMut, fontFamily: fBody, fontSize: 11,
                  }}>
                    Field labels Coast recognises + accepted cell formats
                  </span>
                  <span style={{ flex: 1 }} />
                  <span style={{
                    color: textMut, fontSize: 11,
                    transform: formatHelpOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s', display: 'inline-block',
                  }}>
                    ▶
                  </span>
                </button>
                {formatHelpOpen && (
                  <div style={{
                    padding: '4px 18px 16px',
                    fontFamily: fBody, fontSize: 12, color: text,
                    lineHeight: 1.6,
                  }}>
                    <p style={{ margin: '6px 0 12px 0' }}>
                      Add new properties as new <strong>columns</strong> on the right
                      (or new <strong>rows</strong> at the bottom — orientation is
                      auto-detected on each refresh). Click <strong>REFRESH</strong>{' '}
                      after you edit and Coast re-pulls the latest CSV.
                    </p>

                    <div style={{
                      color: gold, fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.18em', textTransform: 'uppercase',
                      marginTop: 12, marginBottom: 6,
                    }}>
                      Recognised field labels
                    </div>
                    <table style={{
                      width: '100%', borderCollapse: 'collapse',
                      fontSize: 11,
                    }}>
                      <thead>
                        <tr style={{ background: surfaceHi }}>
                          <th style={{ textAlign: 'left', padding: '5px 8px', color: textMut, fontWeight: 700, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' }}>label in your sheet</th>
                          <th style={{ textAlign: 'left', padding: '5px 8px', color: textMut, fontWeight: 700, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' }}>example cell</th>
                          <th style={{ textAlign: 'left', padding: '5px 8px', color: textMut, fontWeight: 700, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' }}>used for</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ['Building type / built',          'Apt, 2023',                              'building_type + year_built'],
                          ['Units / floor',                  '123 units / 7 floors',                   'units (text)'],
                          ['Location',                       '5935 W Pico Blvd, LA, CA 90035',         'location'],
                          ['Types of apts offered',          'Studio, 1bd, 2bd',                       'mix shown in tables'],
                          ['What amenities are in the apts', 'AC, dishwasher, hardwood',               'in-unit amenities'],
                          ['Building amenities',             'pool, gym, clubhouse',                   'building amenities'],
                          ['Pet policy',                     '500 deposit, 50/mo',                     'pet policy'],
                          ['Staff/management on site',       'yes / no / doorman',                     'mgmt'],
                          ['Lease options',                  '12-15 mo, short term',                   'lease options'],
                          ['Studio sqft',                    '477  or  545-561  or  526, 525, 565',    'sqft pool'],
                          ['Studio Monthly',                 '2645  or  $2,645  or  526, 2369 | 565, 2552',         'rent pool'],
                          ['1B1B sqft',                      '609 - 635  or  607, 628, 668',           'sqft pool'],
                          ['1B1B Monthly',                   '2926  or  607: 2295, 628: 2320',         'rent pool'],
                          ['2B2B sqft',                      '973-1110  or  843, 880, 906',            'sqft pool'],
                          ['2B2B Monthly',                   '3300  or  843: 3300, 3300, 3500',        'rent pool'],
                          ['Compares to average 1bd, mid city', 'Avrg: 1692',                          'area avg (vs your rent)'],
                          ['Compares to average 2bd, mid city', 'Avrg: 2497',                          'area avg (vs your rent)'],
                          ['Compares to average 1bd, la',    'Avrg: 2184',                             'area avg (vs your rent)'],
                          ['Compares to average 2bd, la',    'Avrg: 3022',                             'area avg (vs your rent)'],
                          ['How much you need for studio for a yr', '...= 31710 post tax...',          'annual income'],
                          ['How much you need for 1B1B for a yr',   '...= 120,013 post tax',           'annual income'],
                          ['How much you need for 2B2B for a yr',   '...141,000 post tax',             'annual income'],
                        ].map(([lbl, ex, use]) => (
                          <tr key={lbl} style={{ borderBottom: `1px solid ${border}55` }}>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: text, fontWeight: 600, whiteSpace: 'nowrap' }}>{lbl}</td>
                            <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: textSec, fontSize: 10 }}>{ex}</td>
                            <td style={{ padding: '4px 8px', color: textMut, fontSize: 10 }}>{use}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div style={{
                      color: gold, fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.18em', textTransform: 'uppercase',
                      marginTop: 18, marginBottom: 6,
                    }}>
                      Cell-format rules
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: text, fontSize: 12, lineHeight: 1.7 }}>
                      <li><strong>Numbers</strong> can be plain (<code>3300</code>), $-prefixed (<code>$3,300</code>), or include a unit (<code>$3,300/mo</code>). All three parse the same.</li>
                      <li><strong>Multiple unit listings in one cell</strong> use either commas, pipes, or colons:
                        <code style={{ marginLeft: 6 }}>843: 3300, 3300, 3500</code> means sqft 843 with three rents,
                        and <code>526, 2369 | 525, 2511</code> means two units (sqft+rent each).
                        Coast tells the difference using ranges (sqft 100-10,000; rent $1,500-$50,000).</li>
                      <li><strong>Ranges</strong> work: <code>2640-6610</code> or <code>$2,500-$3,000</code>.</li>
                      <li><strong>N/A</strong>: leave the cell blank, or use <code>x</code>, <code>unavailable</code>, <code>n/a</code>, <code>tbd</code>, or <code>-</code>. Coast treats them all as missing.</li>
                      <li><strong>Compares to average</strong> cells: include <code>Avrg: 1692</code> (or just a single bare number) so Coast extracts the area average.</li>
                      <li><strong>Don't put</strong> sqft values in a Monthly column or rent values in a sqft column without their pair — Coast may misclassify a stand-alone <code>843</code> in a 2B2B Monthly cell as below-rent-range and skip it.</li>
                    </ul>

                    <div style={{
                      color: gold, fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.18em', textTransform: 'uppercase',
                      marginTop: 18, marginBottom: 6,
                    }}>
                      Workflow when you add data
                    </div>
                    <ol style={{ margin: 0, paddingLeft: 18, color: text, fontSize: 12, lineHeight: 1.7 }}>
                      <li>Edit your Google Sheet (add columns/rows, change cells).</li>
                      <li>Google republishes the CSV automatically within ~5 min.</li>
                      <li>In Coast, click the gold <strong>REFRESH</strong> button next to your sheet.</li>
                      <li>Open <strong>DIAGNOSTICS</strong> to confirm new fields classified correctly. Anything in the red "Unmatched" list isn't being read into rankings — rename it to one of the labels above, or send the unmatched list back and I'll extend the matcher.</li>
                    </ol>
                  </div>
                )}
              </div>

              {/* Connect form */}
              <div style={{
                background: surface, border: `1px solid ${border}`,
                padding: '12px 14px', marginBottom: 14,
              }}>
                <div style={{
                  color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8,
                }}>
                  Connect a sheet
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    value={connectLabel}
                    onChange={e => setConnectLabel(e.target.value)}
                    placeholder="Label (e.g. Mid-City LA comps)"
                    style={{
                      fontFamily: fBody, fontSize: 12, color: text,
                      border: `1px solid ${border}`, padding: '7px 10px', outline: 'none',
                    }}
                  />
                  <input
                    value={connectUrl}
                    onChange={e => setConnectUrl(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/.../pub?output=csv"
                    style={{
                      fontFamily: fBody, fontSize: 12, color: text,
                      border: `1px solid ${border}`, padding: '7px 10px', outline: 'none',
                    }}
                  />
                  {connectErr && (
                    <div style={{
                      color: red, fontFamily: fBody, fontSize: 11,
                    }}>
                      {connectErr}
                    </div>
                  )}
                  <div>
                    <button
                      onClick={connectSheet}
                      disabled={connectBusy || !connectUrl.trim()}
                      style={{
                        background: connectUrl.trim() ? gold : 'transparent',
                        color:      connectUrl.trim() ? '#fff' : gold,
                        border: `1px solid ${gold}`,
                        fontFamily: fBody, fontSize: 11, fontWeight: 700,
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                        padding: '7px 18px',
                        cursor: connectUrl.trim() ? 'pointer' : 'default',
                        opacity: connectBusy ? 0.6 : 1,
                      }}>
                      {connectBusy ? 'Connecting…' : 'Connect Sheet'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Connected sheets */}
              {!sheetsLoaded ? (
                <div style={{ color: textMut, fontFamily: fBody, fontSize: 13 }}>Loading…</div>
              ) : sheets.length === 0 ? (
                <div style={{
                  color: textMut, fontFamily: fBody, fontSize: 13,
                  fontStyle: 'italic', padding: '24px 0', textAlign: 'center',
                  border: `1px dashed ${border}`,
                }}>
                  No sheets connected yet.
                </div>
              ) : (
                <>
                  {/* Sheet pills */}
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12,
                  }}>
                    {sheets.map(s => {
                      const active = activeSheetId === s.id;
                      return (
                        <span key={s.id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: active ? gold : '#fff',
                          color: active ? '#fff' : text,
                          border: `1px solid ${active ? gold : border}`,
                          fontFamily: fBody, fontSize: 11,
                          fontWeight: active ? 700 : 500,
                          padding: '4px 10px',
                        }}>
                          <button
                            onClick={() => setActiveSheetId(s.id)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: 'inherit', fontFamily: 'inherit', fontSize: 'inherit',
                              fontWeight: 'inherit', padding: 0,
                            }}>
                            {s.label} · {s.row_count} rows
                          </button>
                          <button
                            onClick={() => disconnectSheet(s.id)}
                            title="Disconnect"
                            style={{
                              background: 'none', border: 'none',
                              color: 'inherit', cursor: 'pointer',
                              fontSize: 14, lineHeight: 1, padding: 0,
                            }}>
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>

                  {activeSheetId !== null && (() => {
                    const s = sheets.find(x => x.id === activeSheetId);
                    if (!s) return null;
                    return (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          marginBottom: 12,
                        }}>
                          <div style={{
                            color: textMut, fontFamily: fBody, fontSize: 11,
                          }}>
                            Last fetched {s.last_fetched_at
                              ? new Date(s.last_fetched_at).toLocaleString()
                              : '—'}
                            {s.last_status && ` · ${s.last_status}`}
                          </div>
                          <button
                            onClick={() => refreshSheet(s.id)}
                            disabled={propsBusy}
                            style={{
                              background: 'transparent',
                              border: `1px solid ${gold}`,
                              color: gold, fontFamily: fBody, fontSize: 10,
                              fontWeight: 700, letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              padding: '4px 10px', cursor: 'pointer',
                              opacity: propsBusy ? 0.6 : 1,
                            }}>
                            {propsBusy ? 'Working…' : 'Refresh'}
                          </button>
                          <button
                            onClick={() => {
                              const firstRowIdx =
                                properties[0]?._row_index !== undefined
                                  ? Number(properties[0]._row_index)
                                  : null;
                              const ri = focalRowIdx !== null
                                ? focalRowIdx
                                : firstRowIdx;
                              if (ri !== null && properties.length > 0) {
                                runDiscoverNearby(s.id, ri);
                              } else {
                                setDiscoverErr('Connect a sheet with at least one property first.');
                                setDiscoverOpen(true);
                              }
                            }}
                            disabled={propsBusy}
                            title="Find nearby apartment buildings via OpenStreetMap"
                            style={{
                              background: gold,
                              border: `1px solid ${gold}`,
                              color: '#fff', fontFamily: fBody, fontSize: 10,
                              fontWeight: 700, letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              padding: '4px 12px', cursor: 'pointer',
                              opacity: propsBusy ? 0.6 : 1,
                            }}>
                            Discover Nearby
                          </button>
                          <span style={{ flex: 1 }} />
                          {(['rankings', 'table', 'peers', 'diagnostics'] as const).map(v => {
                            const va = propView === v;
                            return (
                              <button key={v} onClick={() => setPropView(v)}
                                style={{
                                  background: va ? text : 'transparent',
                                  color: va ? '#fff' : text,
                                  border: `1px solid ${va ? text : border}`,
                                  fontFamily: fBody, fontSize: 10, fontWeight: 700,
                                  letterSpacing: '0.1em', textTransform: 'uppercase',
                                  padding: '4px 10px', cursor: 'pointer',
                                }}>
                                {v}
                              </button>
                            );
                          })}
                        </div>

                        {s.last_status === 'error' && s.last_error && (
                          <div style={{
                            color: red, fontFamily: fBody, fontSize: 11,
                            background: '#fef2f0', border: `1px solid ${red}55`,
                            padding: '8px 12px', marginBottom: 12,
                          }}>
                            Last fetch failed: {s.last_error}
                          </div>
                        )}

                        {propView === 'diagnostics' ? (
                          !diagnostics ? (
                            <div style={{
                              color: textMut, fontFamily: fBody, fontSize: 12,
                              fontStyle: 'italic', padding: '12px 0',
                            }}>
                              No diagnostics available.
                            </div>
                          ) : (
                            <div>
                              <div style={{
                                background: surfaceHi,
                                border: `1px solid ${border}`,
                                borderLeft: `3px solid ${gold}`,
                                padding: '10px 14px', marginBottom: 14,
                                fontFamily: fBody, fontSize: 12, color: text,
                              }}>
                                <strong style={{ fontFamily: fHead, color: text }}>
                                  Layout detected:
                                </strong>{' '}
                                {s.orientation === 'tall'
                                  ? 'tall (one property per column, field labels in column 0). Coast auto-transposes before parsing.'
                                  : 'wide (one property per row, headers in row 0).'}
                                {' '}{s.row_count} propert{s.row_count === 1 ? 'y' : 'ies'} parsed.
                              </div>
                              <div style={{
                                color: textMut, fontFamily: fBody, fontSize: 11,
                                lineHeight: 1.55, marginBottom: 14,
                              }}>
                                Each header in your sheet is shown with the canonical field
                                Coast mapped it to. Anything in the "unmatched" list isn't
                                being read into rankings. Rename the column in your sheet to
                                a recognised pattern (e.g. "1B1B Monthly", "Studio sqft",
                                "Compares to average 1bd, LA"), or send the unmatched list
                                back to me and I'll extend the matcher.
                              </div>

                              {/* Issues panel — cells that didn't parse cleanly */}
                              {diagnostics.issues && diagnostics.issues.length > 0 && (
                                <>
                                  <div style={{
                                    color: red, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                                    letterSpacing: '0.18em', textTransform: 'uppercase',
                                    marginTop: 4, marginBottom: 6,
                                  }}>
                                    Cells to fix ({diagnostics.issues.length})
                                  </div>
                                  <div style={{
                                    color: textMut, fontFamily: fBody, fontSize: 11,
                                    lineHeight: 1.55, marginBottom: 10,
                                  }}>
                                    These cells have content but Coast couldn't decide which
                                    number is the answer. Apply the suggested fix in your
                                    Google Sheet, then click <strong>REFRESH</strong>.
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                                    {diagnostics.issues.map((iss, i) => (
                                      <div key={i} style={{
                                        background: '#fef2f0',
                                        border: `1px solid ${red}55`,
                                        borderLeft: `3px solid ${red}`,
                                        padding: '10px 14px',
                                      }}>
                                        <div style={{
                                          fontFamily: fHead, color: text, fontSize: 13,
                                          fontWeight: 800, marginBottom: 2,
                                        }}>
                                          {iss.property} <span style={{
                                            fontFamily: fBody, color: textMut, fontSize: 10,
                                            fontWeight: 600, letterSpacing: '0.08em',
                                            textTransform: 'uppercase', marginLeft: 6,
                                          }}>· {iss.field}</span>
                                        </div>
                                        <div style={{
                                          fontFamily: 'monospace', fontSize: 11, color: textSec,
                                          background: '#fff', border: `1px solid ${border}`,
                                          padding: '4px 8px', margin: '6px 0',
                                          wordBreak: 'break-word',
                                        }}>
                                          {iss.raw_cell}
                                        </div>
                                        <div style={{
                                          color: textMut, fontFamily: fBody, fontSize: 11,
                                          lineHeight: 1.5,
                                        }}>
                                          <strong style={{ color: red }}>Why skipped:</strong>{' '}
                                          {iss.reason}
                                        </div>
                                        <div style={{
                                          color: text, fontFamily: fBody, fontSize: 11,
                                          lineHeight: 1.5, marginTop: 3,
                                        }}>
                                          <strong style={{ color: green }}>To fix:</strong>{' '}
                                          {iss.fix}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                gap: 8, marginBottom: 16,
                              }}>
                                {Object.entries(diagnostics.matched_fields).sort((a, b) => b[1] - a[1]).map(([f, n]) => (
                                  <div key={f} style={{
                                    background: surface,
                                    border: `1px solid ${border}`,
                                    borderLeft: `3px solid ${green}`,
                                    padding: '6px 10px',
                                  }}>
                                    <div style={{
                                      color: green, fontFamily: fBody, fontSize: 9,
                                      letterSpacing: '0.08em', textTransform: 'uppercase',
                                      fontWeight: 700,
                                    }}>
                                      matched
                                    </div>
                                    <div style={{
                                      color: text, fontFamily: 'monospace',
                                      fontSize: 11, marginTop: 2,
                                    }}>
                                      {f}
                                    </div>
                                    <div style={{
                                      color: textMut, fontFamily: fBody, fontSize: 10,
                                      marginTop: 1,
                                    }}>
                                      {n} column{n === 1 ? '' : 's'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div style={{
                                color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                                letterSpacing: '0.18em', textTransform: 'uppercase',
                                marginBottom: 8,
                              }}>
                                Column map ({diagnostics.column_map.length})
                              </div>
                              <div style={{
                                border: `1px solid ${border}`, marginBottom: 16,
                                maxHeight: 280, overflowY: 'auto',
                              }}>
                                <table style={{
                                  width: '100%', borderCollapse: 'collapse',
                                  fontFamily: fBody, fontSize: 11,
                                }}>
                                  <tbody>
                                    {diagnostics.column_map.map((c, i) => (
                                      <tr key={i} style={{
                                        borderBottom: `1px solid ${border}`,
                                        background: c.field ? '#fff' : '#fef9ea',
                                      }}>
                                        <td style={{
                                          padding: '4px 10px', color: text, fontWeight: 600,
                                        }}>
                                          {c.header}
                                        </td>
                                        <td style={{
                                          padding: '4px 10px',
                                          color: c.field ? green : red,
                                          fontFamily: 'monospace',
                                          textAlign: 'right',
                                        }}>
                                          {c.field || '— unmatched'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {diagnostics.unmatched.length > 0 && (
                                <>
                                  <div style={{
                                    color: red, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                                    letterSpacing: '0.18em', textTransform: 'uppercase',
                                    marginBottom: 6,
                                  }}>
                                    Unmatched ({diagnostics.unmatched.length})
                                  </div>
                                  <div style={{
                                    color: text, fontFamily: 'monospace', fontSize: 11,
                                    background: '#fef2f0', border: `1px solid ${red}55`,
                                    padding: '8px 12px', lineHeight: 1.7,
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                    marginBottom: 16,
                                  }}>
                                    {diagnostics.unmatched.join('\n')}
                                  </div>
                                </>
                              )}

                              {/* Sample-row trace: shows which RAW header each
                                  canonical field's value came from per property.
                                  Smoking-gun view for "rent looks wrong" issues. */}
                              {diagnostics.sample_rows && diagnostics.sample_rows.length > 0 && (
                                <>
                                  <div style={{
                                    color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                                    letterSpacing: '0.18em', textTransform: 'uppercase',
                                    marginBottom: 8,
                                  }}>
                                    Per-property extraction trace (first {diagnostics.sample_rows.length})
                                  </div>
                                  <div style={{
                                    color: textMut, fontFamily: fBody, fontSize: 11,
                                    lineHeight: 1.55, marginBottom: 10,
                                  }}>
                                    For each of the first few properties: each canonical field
                                    with the value Coast extracted PLUS the raw cell(s) that value
                                    came from. If a rent looks wrong, this tells you exactly which
                                    column it was pulled from.
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {diagnostics.sample_rows.map((sr, i) => (
                                      <div key={`${sr.row_index}-${i}`} style={{
                                        background: surface, border: `1px solid ${border}`,
                                        borderLeft: `3px solid ${gold}`, padding: '10px 14px',
                                      }}>
                                        <div style={{
                                          fontFamily: fHead, color: text, fontSize: 13,
                                          fontWeight: 800, marginBottom: 8,
                                        }}>
                                          {sr.name}
                                        </div>
                                        <table style={{
                                          width: '100%', borderCollapse: 'collapse',
                                          fontFamily: 'monospace', fontSize: 11,
                                        }}>
                                          <tbody>
                                            {sr.fields.map(f => {
                                              const parsedDisplay = f.parsed === null || f.parsed === undefined
                                                ? '—'
                                                : Array.isArray(f.parsed)
                                                  ? `[${(f.parsed as unknown[]).map(String).join(', ')}]`
                                                  : String(f.parsed);
                                              const empty = parsedDisplay === '—';
                                              return (
                                                <tr key={f.field} style={{
                                                  borderBottom: `1px solid ${border}55`,
                                                }}>
                                                  <td style={{
                                                    padding: '3px 8px',
                                                    color: empty ? textMut : text,
                                                    fontWeight: 600,
                                                    width: 130,
                                                    verticalAlign: 'top',
                                                  }}>
                                                    {f.field}
                                                  </td>
                                                  <td style={{
                                                    padding: '3px 8px',
                                                    color: empty ? textMut : green,
                                                    fontWeight: 700,
                                                    width: 100,
                                                    verticalAlign: 'top',
                                                  }}>
                                                    {parsedDisplay}
                                                  </td>
                                                  <td style={{
                                                    padding: '3px 8px',
                                                    color: textMut,
                                                    verticalAlign: 'top',
                                                    wordBreak: 'break-word',
                                                  }}>
                                                    {f.source_cells.length === 0 ? '(no source col matched)' : f.source_cells.join('  •  ')}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          )
                        ) : propView === 'peers' ? (
                          properties.length === 0 ? (
                            <div style={{
                              color: textMut, fontFamily: fBody, fontSize: 12,
                              fontStyle: 'italic', padding: '12px 0',
                            }}>
                              No properties to compare.
                            </div>
                          ) : (() => {
                            // Determine focal building
                            const focal = (focalRowIdx !== null
                              ? properties.find(p => Number(p._row_index) === focalRowIdx)
                              : properties[0]) || properties[0];
                            const focalRi = String(focal._row_index ?? '');
                            const peers = similarity?.peers?.[focalRi] || [];
                            const peerProps = peers
                              .map(pr => ({
                                meta: pr,
                                p: properties.find(x => Number(x._row_index) === pr.row_index),
                              }))
                              .filter(x => x.p) as { meta: Peer; p: PropertyRow }[];

                            // Stat extractors
                            const stats = (p: PropertyRow) => ({
                              location:    pickCell(p, 'location', ['location','address','neighborhood']),
                              year:        avgNum(p.year_built),
                              btype:       pickCell(p, 'building_type', ['building type','type']),
                              studio_mo:   avgNum(p.studio_monthly),
                              one_mo:      avgNum(p['1b1b_monthly']),
                              two_mo:      avgNum(p['2b2b_monthly']),
                              studio_sf:   avgNum(p.studio_sqft),
                              one_sf:      avgNum(p['1b1b_sqft']),
                              two_sf:      avgNum(p['2b2b_sqft']),
                            });
                            const focalStats = stats(focal);

                            const rowDef: { label: string; key: keyof ReturnType<typeof stats>; fmt: 'money'|'sqft'|'year'|'text'; lowerIsBetter: boolean }[] = [
                              { label: 'Location',         key: 'location',  fmt: 'text',  lowerIsBetter: false },
                              { label: 'Building type',    key: 'btype',     fmt: 'text',  lowerIsBetter: false },
                              { label: 'Year built',       key: 'year',      fmt: 'year',  lowerIsBetter: false },
                              { label: 'Studio monthly',   key: 'studio_mo', fmt: 'money', lowerIsBetter: true },
                              { label: '1B1B monthly',     key: 'one_mo',    fmt: 'money', lowerIsBetter: true },
                              { label: '2B2B monthly',     key: 'two_mo',    fmt: 'money', lowerIsBetter: true },
                              { label: 'Studio sqft',      key: 'studio_sf', fmt: 'sqft',  lowerIsBetter: false },
                              { label: '1B1B sqft',        key: 'one_sf',    fmt: 'sqft',  lowerIsBetter: false },
                              { label: '2B2B sqft',        key: 'two_sf',    fmt: 'sqft',  lowerIsBetter: false },
                            ];

                            return (
                              <>
                                {/* ───────────────── PERSONA-DRIVEN RANKING ───────────────── */}
                                {comparison && comparison.personas.length > 0 && (
                                  <div style={{
                                    marginBottom: 28,
                                    background: surfaceHi,
                                    border: `1px solid ${border}`,
                                    borderLeft: `4px solid ${gold}`,
                                    padding: '14px 18px 18px',
                                  }}>
                                    <div style={{
                                      color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                                      letterSpacing: '0.22em', textTransform: 'uppercase',
                                      marginBottom: 6,
                                    }}>
                                      What kind of renter are you?
                                    </div>
                                    <div style={{
                                      color: textSec, fontFamily: fBody, fontSize: 12,
                                      lineHeight: 1.5, marginBottom: 12,
                                    }}>
                                      Coast ranks every building in your sheet for each renter
                                      profile. The same building can win for one profile and lose for
                                      another — pick the one that matches how you actually want to live.
                                    </div>
                                    {/* Persona pills */}
                                    <div style={{
                                      display: 'flex', flexWrap: 'wrap', gap: 6,
                                      marginBottom: 14,
                                    }}>
                                      {comparison.personas.map(p => {
                                        const active = activePersona === p.id;
                                        return (
                                          <button key={p.id}
                                            onClick={() => setActivePersona(p.id)}
                                            style={{
                                              background: active ? text : '#fff',
                                              color:      active ? '#fff' : text,
                                              border: `1px solid ${active ? text : border}`,
                                              fontFamily: fHead, fontSize: 13, fontWeight: 700,
                                              letterSpacing: '0.04em',
                                              padding: '6px 14px', cursor: 'pointer',
                                              transition: 'all 0.15s',
                                            }}>
                                            {p.label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                    {(() => {
                                      const persona = comparison.personas.find(p => p.id === activePersona)
                                                    || comparison.personas[0];
                                      const ranking = comparison.rankings[persona.id] || [];
                                      return (
                                        <>
                                          <div style={{
                                            fontFamily: fHead, color: text, fontSize: 16,
                                            fontWeight: 800, marginBottom: 2,
                                          }}>
                                            {persona.label} <span style={{
                                              color: gold, fontFamily: fBody, fontSize: 11,
                                              fontWeight: 700, marginLeft: 6,
                                              letterSpacing: '0.08em', textTransform: 'uppercase',
                                            }}>{persona.tagline}</span>
                                          </div>
                                          <div style={{
                                            color: textSec, fontFamily: fBody, fontSize: 12,
                                            lineHeight: 1.5, marginBottom: 6,
                                          }}>
                                            {persona.desc}
                                          </div>
                                          <div style={{
                                            color: gold, fontFamily: fBody, fontSize: 10, fontWeight: 700,
                                            letterSpacing: '0.18em', textTransform: 'uppercase',
                                            marginBottom: 12,
                                          }}>
                                            Ranked by: {persona.primary_label}
                                          </div>

                                          {/* Fact-based ranking list */}
                                          <div style={{
                                            display: 'flex', flexDirection: 'column', gap: 8,
                                          }}>
                                            {ranking.map((r, rIdx) => {
                                              const isTop = rIdx === 0;
                                              const hasPrimary = r.primary_value !== null;
                                              return (
                                                <div key={`${r.row_index}-${rIdx}`} style={{
                                                  display: 'grid',
                                                  gridTemplateColumns: '34px 1fr auto',
                                                  gap: 14, alignItems: 'baseline',
                                                  background: '#fff',
                                                  border: `1px solid ${isTop ? gold : border}`,
                                                  borderLeft: `4px solid ${isTop ? gold : (hasPrimary ? gold + '55' : border)}`,
                                                  padding: '10px 16px',
                                                  opacity: hasPrimary ? 1 : 0.55,
                                                }}>
                                                  <div style={{
                                                    fontFamily: fHead, color: isTop ? gold : textMut,
                                                    fontSize: 22, fontWeight: 800,
                                                    textAlign: 'center',
                                                  }}>
                                                    {rIdx + 1}
                                                  </div>
                                                  <div>
                                                    <div style={{
                                                      fontFamily: fHead, color: text, fontSize: 16,
                                                      fontWeight: 800, marginBottom: 4,
                                                    }}>
                                                      {r.name}
                                                    </div>
                                                    {r.facts.length > 0 && (
                                                      <div style={{
                                                        display: 'flex', flexWrap: 'wrap',
                                                        gap: '4px 14px',
                                                        color: textSec, fontFamily: fBody,
                                                        fontSize: 11, lineHeight: 1.6,
                                                      }}>
                                                        {r.facts.map((f, j) => (
                                                          <span key={j}>
                                                            <span style={{
                                                              color: textMut, fontWeight: 600,
                                                            }}>
                                                              {f.label}:
                                                            </span>{' '}
                                                            <span style={{ color: text }}>
                                                              {f.value}
                                                            </span>
                                                          </span>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div style={{
                                                    textAlign: 'right',
                                                    minWidth: 110,
                                                  }}>
                                                    <div style={{
                                                      fontFamily: fHead,
                                                      color: hasPrimary ? gold : textMut,
                                                      fontSize: 16, fontWeight: 800, lineHeight: 1.1,
                                                    }}>
                                                      {r.primary_display}
                                                    </div>
                                                    <div style={{
                                                      color: textMut, fontFamily: fBody, fontSize: 9,
                                                      letterSpacing: '0.1em', textTransform: 'uppercase',
                                                      marginTop: 3,
                                                    }}>
                                                      {persona.primary_label}
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}

                                {/* ───────────────── PER-BUILDING NICHES ───────────────── */}
                                {comparison && comparison.niches.filter(n => n.leading.length).length > 0 && (
                                  <div style={{ marginBottom: 28 }}>
                                    <div style={{
                                      color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                                      letterSpacing: '0.22em', textTransform: 'uppercase',
                                      marginBottom: 4,
                                    }}>
                                      Each building's niche
                                    </div>
                                    <div style={{
                                      color: textSec, fontFamily: fBody, fontSize: 12,
                                      lineHeight: 1.5, marginBottom: 12,
                                    }}>
                                      Where each building leads the rest of your sheet — its unique
                                      strength worth knowing before you tour.
                                    </div>
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                      gap: 10,
                                    }}>
                                      {comparison.niches
                                        .filter(n => n.leading.length > 0)
                                        .map((n, i) => (
                                        <div key={i} style={{
                                          background: '#fff',
                                          border: `1px solid ${border}`,
                                          borderLeft: `3px solid ${gold}`,
                                          padding: '11px 14px',
                                        }}>
                                          <div style={{
                                            fontFamily: fHead, color: text, fontSize: 14,
                                            fontWeight: 800, marginBottom: 4,
                                          }}>
                                            {n.name}
                                          </div>
                                          {n.leading.map((lead, j) => (
                                            <div key={j} style={{ marginBottom: 4 }}>
                                              <div style={{
                                                color: gold, fontFamily: fBody, fontSize: 9,
                                                fontWeight: 700, letterSpacing: '0.14em',
                                                textTransform: 'uppercase',
                                              }}>
                                                {lead.label}
                                              </div>
                                              <div style={{
                                                color: textSec, fontFamily: fBody, fontSize: 11,
                                                lineHeight: 1.45,
                                              }}>
                                                {lead.detail}
                                              </div>
                                            </div>
                                          ))}
                                          {n.income_min !== null && (
                                            <div style={{
                                              color: textMut, fontFamily: fBody, fontSize: 10,
                                              marginTop: 6, paddingTop: 6,
                                              borderTop: `1px solid ${border}`,
                                            }}>
                                              ~${Math.round(n.income_min).toLocaleString()}/yr income needed
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* ───────────────── HEAD-TO-HEAD COMPARISON (existing) ──── */}
                                <div style={{
                                  color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                                  letterSpacing: '0.22em', textTransform: 'uppercase',
                                  marginBottom: 4,
                                }}>
                                  Head-to-head
                                </div>
                                <div style={{
                                  color: textMut, fontFamily: fBody, fontSize: 11,
                                  lineHeight: 1.55, marginBottom: 14,
                                }}>
                                  Pick a focal building. Coast finds its 3 most-similar peers
                                  from your sheet and shows side-by-side stats with
                                  green/red deltas (green = better for renter — lower rent or
                                  more sqft; red = worse).
                                </div>

                                {/* Focal selector + Discover Nearby */}
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: 10,
                                  marginBottom: 16, flexWrap: 'wrap',
                                }}>
                                  <span style={{
                                    color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                                    letterSpacing: '0.18em', textTransform: 'uppercase',
                                  }}>
                                    Focal building
                                  </span>
                                  <select
                                    value={String(focal._row_index ?? '')}
                                    onChange={e => setFocalRowIdx(Number(e.target.value))}
                                    style={{
                                      fontFamily: fBody, fontSize: 13, color: text,
                                      border: `1px solid ${border}`, padding: '6px 10px',
                                      background: '#fff', minWidth: 220,
                                    }}>
                                    {properties.map(p => (
                                      <option key={p._row_index} value={String(p._row_index)}>
                                        {propName(p)}
                                      </option>
                                    ))}
                                  </select>
                                  <span style={{ flex: 1 }} />
                                  <span style={{
                                    color: textMut, fontFamily: fBody, fontSize: 10,
                                  }}>
                                    radius
                                  </span>
                                  <select
                                    value={discoverRadius}
                                    onChange={e => setDiscoverRadius(Number(e.target.value))}
                                    style={{
                                      fontFamily: fBody, fontSize: 11, color: text,
                                      border: `1px solid ${border}`, padding: '4px 8px',
                                      background: '#fff',
                                    }}>
                                    {[0.5, 1, 2, 3, 5, 8, 10].map(r => (
                                      <option key={r} value={r}>{r} mi</option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => {
                                      const ri = focal._row_index;
                                      if (activeSheetId !== null && ri !== undefined) {
                                        runDiscoverNearby(activeSheetId, Number(ri));
                                      }
                                    }}
                                    style={{
                                      background: gold,
                                      color: '#fff',
                                      border: `1px solid ${gold}`,
                                      fontFamily: fBody, fontSize: 10, fontWeight: 700,
                                      letterSpacing: '0.12em', textTransform: 'uppercase',
                                      padding: '6px 14px', cursor: 'pointer',
                                    }}>
                                    Discover Nearby
                                  </button>
                                </div>

                                {peerProps.length === 0 ? (
                                  <div style={{
                                    color: textMut, fontFamily: fBody, fontSize: 12,
                                    fontStyle: 'italic', padding: '12px 0',
                                    border: `1px dashed ${border}`, textAlign: 'center',
                                  }}>
                                    No comparable peers in this sheet (only one building parsed?).
                                  </div>
                                ) : (
                                  <div style={{ overflowX: 'auto' }}>
                                    <table style={{
                                      width: '100%', borderCollapse: 'collapse',
                                      fontFamily: fBody, fontSize: 12, color: text,
                                      minWidth: 600,
                                    }}>
                                      <thead>
                                        <tr style={{ background: surfaceHi }}>
                                          <th style={{
                                            textAlign: 'left', padding: '8px 10px',
                                            borderBottom: `1px solid ${border}`,
                                            color: textMut, fontSize: 10, fontWeight: 700,
                                            letterSpacing: '0.1em', textTransform: 'uppercase',
                                            width: 130,
                                          }}>
                                            metric
                                          </th>
                                          <th style={{
                                            textAlign: 'left', padding: '8px 10px',
                                            borderBottom: `2px solid ${gold}`,
                                            color: text, fontSize: 12, fontWeight: 800,
                                            fontFamily: fHead,
                                          }}>
                                            {propName(focal)}
                                            <div style={{
                                              color: gold, fontSize: 9, fontWeight: 700,
                                              letterSpacing: '0.14em', textTransform: 'uppercase',
                                            }}>
                                              focal
                                            </div>
                                          </th>
                                          {peerProps.map(({ meta, p }) => (
                                            <th key={meta.row_index} style={{
                                              textAlign: 'left', padding: '8px 10px',
                                              borderBottom: `1px solid ${border}`,
                                              color: text, fontSize: 12, fontWeight: 800,
                                              fontFamily: fHead,
                                            }}>
                                              {propName(p)}
                                              <div style={{
                                                color: textMut, fontSize: 9, fontWeight: 600,
                                                letterSpacing: '0.06em',
                                              }}>
                                                d={meta.distance.toFixed(2)}{meta.why ? ` · ${meta.why}` : ''}
                                              </div>
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {rowDef.map(rd => {
                                          const fv = focalStats[rd.key];
                                          return (
                                            <tr key={rd.label} style={{ borderBottom: `1px solid ${border}` }}>
                                              <td style={{
                                                padding: '6px 10px',
                                                color: textMut, fontSize: 11,
                                                letterSpacing: '0.04em',
                                              }}>
                                                {rd.label}
                                              </td>
                                              <td style={{
                                                padding: '6px 10px',
                                                color: text, fontWeight: 600,
                                              }}>
                                                {rd.fmt === 'money'
                                                  ? (typeof fv === 'number' ? `$${Math.round(fv).toLocaleString()}` : '—')
                                                  : rd.fmt === 'sqft'
                                                    ? (typeof fv === 'number' ? `${Math.round(fv).toLocaleString()} sqft` : '—')
                                                    : rd.fmt === 'year'
                                                      ? (typeof fv === 'number' ? String(Math.round(fv)) : '—')
                                                      : (fv ? String(fv) : '—')}
                                              </td>
                                              {peerProps.map(({ meta, p }) => {
                                                const ps = stats(p);
                                                const pv = ps[rd.key];
                                                if (rd.fmt === 'text') {
                                                  return (
                                                    <td key={meta.row_index} style={{
                                                      padding: '6px 10px',
                                                      color: text,
                                                    }}>
                                                      {pv ? String(pv) : '—'}
                                                    </td>
                                                  );
                                                }
                                                const d = deltaCell(
                                                  typeof fv === 'number' ? fv : null,
                                                  typeof pv === 'number' ? pv : null,
                                                  rd.fmt,
                                                  rd.lowerIsBetter,
                                                );
                                                return (
                                                  <td key={meta.row_index} style={{
                                                    padding: '6px 10px',
                                                    color: d.color, fontWeight: 500,
                                                  }}>
                                                    {d.display}
                                                  </td>
                                                );
                                              })}
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </>
                            );
                          })()
                        ) : propView === 'rankings' ? (
                          rankings.length === 0 ? (
                            <div style={{
                              color: textMut, fontFamily: fBody, fontSize: 12,
                              fontStyle: 'italic', padding: '12px 0',
                            }}>
                              No rankings yet — sheet may be empty or columns weren't recognised.
                            </div>
                          ) : (
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                              gap: 12,
                            }}>
                              {rankings.map(r => (
                                <div key={r.label} style={{
                                  background: surface,
                                  border: `1px solid ${border}`,
                                  borderLeft: `3px solid ${gold}`,
                                  padding: '10px 12px',
                                }}>
                                  <div style={{
                                    color: textMut, fontFamily: fBody, fontSize: 9,
                                    letterSpacing: '0.12em', textTransform: 'uppercase',
                                    marginBottom: 6,
                                  }}>
                                    {r.label}
                                  </div>
                                  <ol style={{
                                    margin: 0, paddingLeft: 18,
                                    fontFamily: fBody, fontSize: 12, color: text,
                                    lineHeight: 1.5,
                                  }}>
                                    {r.ranked.slice(0, 5).map((row, i) => (
                                      <li key={`${row.row_index}-${i}`}>
                                        <span style={{ color: text, fontWeight: 600 }}>
                                          {row.name}
                                        </span>
                                        {' — '}
                                        <span style={{ color: green, fontWeight: 700 }}>
                                          {fmtRanked(row.value, r.metric_unit)}
                                        </span>
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              ))}
                            </div>
                          )
                        ) : (
                          properties.length === 0 ? (
                            <div style={{
                              color: textMut, fontFamily: fBody, fontSize: 12,
                              fontStyle: 'italic', padding: '12px 0',
                            }}>
                              No property rows parsed.
                            </div>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{
                                width: '100%', borderCollapse: 'collapse',
                                fontFamily: fBody, fontSize: 11, color: text,
                              }}>
                                <thead>
                                  <tr style={{ background: surfaceHi }}>
                                    {[
                                      'Property','Built','Location','Apt types',
                                      'Studio $/mo','1B1B $/mo','2B2B $/mo',
                                      '1B1B sqft','Pet policy','Mgmt'
                                    ].map(h => (
                                      <th key={h} style={{
                                        textAlign: 'left',
                                        padding: '6px 8px',
                                        borderBottom: `1px solid ${border}`,
                                        fontWeight: 700, color: textSec,
                                        fontSize: 10, letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                      }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {properties.map((p, i) => {
                                    const studio = avgNum(p.studio_monthly);
                                    const oneBR  = avgNum(p['1b1b_monthly']);
                                    const twoBR  = avgNum(p['2b2b_monthly']);
                                    const oneSF  = avgNum(p['1b1b_sqft']);
                                    return (
                                      <tr key={i} style={{
                                        borderBottom: `1px solid ${border}`,
                                      }}>
                                        <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                                          {pickCell(p, 'name', ['property','property name','building name','name'])}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                          {pickCell(p, 'year_built', ['built','year built'])}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                          {pickCell(p, 'location', ['location','address','neighborhood'])}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                          {pickCell(p, 'apt_types_offered', ['apt types','unit types','types of apt','apt types offered'])}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                          {studio !== null ? `$${Math.round(studio).toLocaleString()}` : '—'}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                          {oneBR !== null ? `$${Math.round(oneBR).toLocaleString()}` : '—'}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                          {twoBR !== null ? `$${Math.round(twoBR).toLocaleString()}` : '—'}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                          {oneSF !== null ? Math.round(oneSF).toLocaleString() : '—'}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                          {pickCell(p, 'pet_policy', ['pet policy','pets'])}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                          {pickCell(p, 'staff_management', ['staff','management','on site management','onsite staff','staff/management on site','staff management'])}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </>
          )}

          {tab === 'journal' && (
            <>
              <div style={{ marginBottom: 16 }}>
                <textarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') saveEntry();
                  }}
                  placeholder="What did you learn today? What did you observe? What questions came up? Cmd+Enter to save."
                  rows={3}
                  style={{
                    width: '100%', fontFamily: fBody, fontSize: 14,
                    color: text, background: '#fff',
                    border: `1px solid ${border}`,
                    padding: '10px 14px', outline: 'none', resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'center' }}>
                  <input
                    value={draftTags}
                    onChange={e => setDraftTags(e.target.value)}
                    placeholder="tags (e.g. acquisitions, week 1, denver)"
                    style={{
                      flex: 1, fontFamily: fBody, fontSize: 12,
                      color: text, border: `1px solid ${border}`,
                      padding: '6px 10px', outline: 'none',
                    }}
                  />
                  <button
                    onClick={saveEntry}
                    disabled={savingEntry || !draft.trim()}
                    style={{
                      background: !draft.trim() ? 'transparent' : gold,
                      color: !draft.trim() ? gold : '#fff',
                      border: `1px solid ${gold}`,
                      fontFamily: fBody, fontSize: 12, fontWeight: 700,
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      padding: '7px 18px',
                      cursor: !draft.trim() ? 'default' : 'pointer',
                      opacity: savingEntry || !draft.trim() ? 0.6 : 1,
                    }}>
                    {savingEntry ? 'Saving…' : 'Add Entry'}
                  </button>
                </div>
              </div>

              {!entriesLoaded ? (
                <div style={{ color: textMut, fontFamily: fBody, fontSize: 13 }}>
                  Loading…
                </div>
              ) : entries.length === 0 ? (
                <div style={{
                  color: textMut, fontFamily: fBody, fontSize: 13,
                  fontStyle: 'italic', padding: '24px 0', textAlign: 'center',
                  border: `1px dashed ${border}`,
                }}>
                  Empty journal. The first entry might be: questions to ask in your first 1:1.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {entries.map(e => (
                    <div key={e.id} style={{
                      background: surface,
                      border: `1px solid ${border}`,
                      borderLeft: `3px solid ${gold}`,
                      padding: '12px 14px',
                      display: 'flex', gap: 12,
                      alignItems: 'flex-start',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: fBody, fontSize: 13, color: text,
                          lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {e.content}
                        </div>
                        <div style={{
                          marginTop: 6, display: 'flex', gap: 12,
                          color: textMut, fontFamily: fBody, fontSize: 10,
                          letterSpacing: '0.04em',
                        }}>
                          <span>{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</span>
                          {e.tags && (
                            <span style={{ color: gold }}>· {e.tags}</span>
                          )}
                        </div>
                      </div>
                      <button onClick={() => deleteEntry(e.id)} title="Delete entry"
                        style={{
                          background: 'none', border: 'none', color: textMut,
                          cursor: 'pointer', fontFamily: fBody, fontSize: 16,
                          padding: '0 4px', lineHeight: 1,
                        }}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'glossary' && (
            <>
              <input
                value={glossaryQ}
                onChange={e => setGlossaryQ(e.target.value)}
                placeholder="Search terms (e.g. cap rate, DSCR, value-add)…"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  fontFamily: fBody, fontSize: 13, color: text,
                  border: `1px solid ${border}`,
                  padding: '8px 12px', outline: 'none', marginBottom: 16,
                }}
              />
              {!glossaryLoaded ? (
                <div style={{ color: textMut, fontFamily: fBody, fontSize: 13 }}>Loading…</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {filteredGlossary.map(g => (
                    <div key={g.term} style={{
                      paddingBottom: 12, borderBottom: `1px solid ${border}`,
                    }}>
                      <div style={{
                        fontFamily: fHead, color: text, fontSize: 16,
                        fontWeight: 800, marginBottom: 4,
                      }}>
                        {g.term}
                      </div>
                      <div style={{
                        fontFamily: fBody, color: textSec, fontSize: 13,
                        lineHeight: 1.6, marginBottom: 6,
                      }}>
                        {g.definition}
                      </div>
                      {g.formula && (
                        <div style={{
                          fontFamily: 'monospace', fontSize: 12,
                          color: textSec, background: surfaceHi,
                          border: `1px solid ${border}`,
                          padding: '6px 10px', marginBottom: 6,
                        }}>
                          {g.formula}
                        </div>
                      )}
                      {g.source && (
                        <div style={{
                          color: textMut, fontFamily: fBody, fontSize: 10,
                          letterSpacing: '0.06em',
                        }}>
                          Source: {g.source}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'reading' && (
            <>
              <p style={{
                fontFamily: fBody, color: textSec, fontSize: 13,
                lineHeight: 1.6, margin: '0 0 18px 0',
              }}>
                Primary-source links covering tribal investment law, capital structures,
                and lending programs. Every link goes to a government, academic, or
                industry-association source — read these for the legal/financial framework
                behind tribally-owned investment vehicles.
              </p>
              {!libraryLoaded ? (
                <div style={{ color: textMut, fontFamily: fBody, fontSize: 13 }}>Loading…</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                  {library.map(sec => (
                    <div key={sec.section}>
                      <div style={{
                        color: gold, fontFamily: fBody, fontSize: 10, fontWeight: 700,
                        letterSpacing: '0.18em', textTransform: 'uppercase',
                        marginBottom: 10,
                      }}>
                        {sec.section}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {sec.items.map(it => (
                          <a key={it.url} href={it.url} target="_blank" rel="noopener noreferrer"
                            style={{
                              display: 'block',
                              padding: '12px 14px',
                              border: `1px solid ${border}`,
                              borderLeft: `3px solid ${gold}`,
                              background: surface,
                              textDecoration: 'none',
                            }}>
                            <div style={{
                              fontFamily: fHead, color: text, fontSize: 14,
                              fontWeight: 700, marginBottom: 4,
                            }}>
                              {it.title}
                            </div>
                            <div style={{
                              fontFamily: fBody, color: textSec, fontSize: 12,
                              lineHeight: 1.55, marginBottom: 6,
                            }}>
                              {it.summary}
                            </div>
                            <div style={{
                              color: textMut, fontFamily: fBody, fontSize: 10,
                              letterSpacing: '0.06em',
                            }}>
                              {it.source}
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Discover Nearby modal */}
      {discoverOpen && (
        <div
          onClick={() => setDiscoverOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(13,61,92,0.55)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '40px 24px', overflowY: 'auto',
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', width: '100%', maxWidth: 980,
              border: `1px solid ${border}`,
              borderTop: `3px solid ${gold}`,
              boxShadow: '0 8px 32px rgba(13,61,92,0.30)',
              display: 'flex', flexDirection: 'column',
            }}>
            <div style={{
              padding: '18px 24px 12px',
              borderBottom: `1px solid ${border}`,
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div>
                <div style={{
                  color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 4,
                }}>
                  Coast · Discovery
                </div>
                <h2 style={{
                  fontFamily: fHead, color: text, fontSize: 20, fontWeight: 800,
                  margin: 0,
                }}>
                  Nearby apartment buildings
                </h2>
              </div>
              <span style={{ flex: 1 }} />
              <button onClick={() => setDiscoverOpen(false)}
                style={{
                  background: 'none', border: `1px solid ${border}`,
                  color: textMut, fontFamily: fBody, fontSize: 11,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  padding: '6px 14px', cursor: 'pointer',
                }}>
                Close
              </button>
            </div>

            <div style={{ padding: '14px 24px 24px', maxHeight: '72vh', overflowY: 'auto' }}>

              {/* Focal picker + radius — change target without closing modal */}
              {activeSheetId !== null && properties.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  marginBottom: 16, flexWrap: 'wrap',
                  paddingBottom: 12, borderBottom: `1px solid ${border}`,
                }}>
                  <span style={{
                    color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.18em', textTransform: 'uppercase',
                  }}>
                    Focal building
                  </span>
                  <select
                    value={String(focalRowIdx ?? properties[0]._row_index ?? '')}
                    onChange={e => setFocalRowIdx(Number(e.target.value))}
                    style={{
                      fontFamily: fBody, fontSize: 13, color: text,
                      border: `1px solid ${border}`, padding: '6px 10px',
                      background: '#fff', minWidth: 200,
                    }}>
                    {properties.map(p => (
                      <option key={p._row_index} value={String(p._row_index)}>
                        {propName(p)}
                      </option>
                    ))}
                  </select>
                  <span style={{
                    color: textMut, fontFamily: fBody, fontSize: 10,
                  }}>
                    radius
                  </span>
                  <select
                    value={discoverRadius}
                    onChange={e => setDiscoverRadius(Number(e.target.value))}
                    style={{
                      fontFamily: fBody, fontSize: 11, color: text,
                      border: `1px solid ${border}`, padding: '4px 8px',
                      background: '#fff',
                    }}>
                    {[0.5, 1, 2, 3, 5, 8, 10].map(r => (
                      <option key={r} value={r}>{r} mi</option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      const ri = focalRowIdx !== null
                        ? focalRowIdx
                        : (properties[0]?._row_index !== undefined
                            ? Number(properties[0]._row_index) : null);
                      if (activeSheetId !== null && ri !== null) {
                        runDiscoverNearby(activeSheetId, ri);
                      }
                    }}
                    disabled={discoverBusy}
                    style={{
                      background: gold, border: `1px solid ${gold}`,
                      color: '#fff', fontFamily: fBody, fontSize: 10,
                      fontWeight: 700, letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      padding: '6px 14px', cursor: 'pointer',
                      opacity: discoverBusy ? 0.6 : 1,
                    }}>
                    {discoverBusy ? 'Searching…' : 'Search'}
                  </button>
                </div>
              )}

              {discoverBusy && (
                <div style={{
                  color: textMut, fontFamily: fBody, fontSize: 13, padding: '20px 0',
                }}>
                  Querying OpenStreetMap…
                </div>
              )}
              {discoverErr && (
                <div style={{
                  color: red, fontFamily: fBody, fontSize: 12,
                  background: '#fef2f0', border: `1px solid ${red}55`,
                  padding: '10px 14px',
                }}>
                  {discoverErr}
                </div>
              )}
              {!discoverBusy && discoverData && (
                <>
                  <div style={{
                    color: textMut, fontFamily: fBody, fontSize: 12,
                    lineHeight: 1.55, marginBottom: 14,
                  }}>
                    <strong style={{ color: text }}>
                      {discoverData.discovered}
                    </strong> apartment buildings within{' '}
                    <strong style={{ color: text }}>{discoverData.radius_miles} mi</strong>
                    {' of '}<em style={{ color: textSec }}>{discoverData.focal.label}</em>.
                    Source: OpenStreetMap. Click a building's search link to research it, then
                    copy the row template to paste into your sheet as a new column.
                  </div>

                  <div style={{
                    color: textSec, fontFamily: fBody, fontSize: 11,
                    background: surfaceHi, border: `1px solid ${border}`,
                    borderLeft: `3px solid ${gold}`,
                    padding: '8px 12px', marginBottom: 14,
                    lineHeight: 1.5,
                  }}>
                    {discoverData.fill_message}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {discoverData.candidates.map((c, i) => {
                      const csvRow = c.row_template
                        .map(rt => `${rt.field}\t${rt.value}`)
                        .join('\n');
                      return (
                        <div key={i} style={{
                          background: surface,
                          border: `1px solid ${border}`,
                          borderLeft: `3px solid ${gold}`,
                          padding: '10px 14px',
                        }}>
                          <div style={{
                            display: 'flex', alignItems: 'baseline', gap: 12,
                            flexWrap: 'wrap',
                          }}>
                            <div style={{
                              fontFamily: fHead, color: text, fontSize: 14,
                              fontWeight: 800,
                            }}>
                              {c.name}
                            </div>
                            <span style={{
                              color: gold, fontFamily: fBody, fontSize: 10, fontWeight: 700,
                              letterSpacing: '0.06em',
                            }}>
                              {c.distance_miles.toFixed(2)} mi
                            </span>
                            {c.year && (
                              <span style={{
                                color: textMut, fontFamily: fBody, fontSize: 10,
                              }}>
                                built {c.year}
                              </span>
                            )}
                            {c.floors && (
                              <span style={{
                                color: textMut, fontFamily: fBody, fontSize: 10,
                              }}>
                                {c.floors} floors
                              </span>
                            )}
                            {c.units && (
                              <span style={{
                                color: textMut, fontFamily: fBody, fontSize: 10,
                              }}>
                                {c.units} units
                              </span>
                            )}
                          </div>
                          {c.address && (
                            <div style={{
                              color: textSec, fontFamily: fBody, fontSize: 11,
                              marginTop: 2,
                            }}>
                              {c.address}
                            </div>
                          )}
                          <div style={{
                            display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap',
                          }}>
                            {(['google','apartments_com','maps'] as const).map(k => {
                              const url = c.search_urls?.[k];
                              if (!url) return null;
                              const lbl = k === 'google' ? 'Google' :
                                          k === 'apartments_com' ? 'Apartments.com' :
                                                                   'Maps';
                              return (
                                <button key={k}
                                  onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                                  style={{
                                    background: '#fff',
                                    color: gold, fontFamily: fBody, fontSize: 10, fontWeight: 700,
                                    letterSpacing: '0.1em', textTransform: 'uppercase',
                                    border: `1px solid ${gold}`, padding: '4px 12px',
                                    cursor: 'pointer',
                                  }}>
                                  {lbl}
                                </button>
                              );
                            })}
                            <button
                              onClick={async () => {
                                let ok = false;
                                try {
                                  if (navigator.clipboard && window.isSecureContext) {
                                    await navigator.clipboard.writeText(csvRow);
                                    ok = true;
                                  } else {
                                    // Fallback for older browsers / non-secure contexts
                                    const ta = document.createElement('textarea');
                                    ta.value = csvRow;
                                    ta.style.position = 'fixed';
                                    ta.style.left = '-9999px';
                                    document.body.appendChild(ta);
                                    ta.focus();
                                    ta.select();
                                    ok = document.execCommand('copy');
                                    document.body.removeChild(ta);
                                  }
                                } catch {
                                  ok = false;
                                }
                                if (ok) {
                                  setCopiedIdx(i);
                                  setTimeout(() => setCopiedIdx(c2 => c2 === i ? null : c2), 2000);
                                } else {
                                  // Show fallback: open a prompt the user can copy from
                                  window.prompt('Press Cmd/Ctrl+C to copy this column:', csvRow);
                                }
                              }}
                              style={{
                                background: copiedIdx === i ? green : '#fff',
                                border: `1px solid ${green}`,
                                color: copiedIdx === i ? '#fff' : green,
                                fontFamily: fBody, fontSize: 10, fontWeight: 700,
                                letterSpacing: '0.1em', textTransform: 'uppercase',
                                padding: '4px 12px', cursor: 'pointer',
                                transition: 'background 0.15s',
                              }}>
                              {copiedIdx === i ? '✓ Copied' : 'Copy column for sheet'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {discoverData.candidates.length === 0 && (
                    <div style={{
                      color: textMut, fontFamily: fBody, fontSize: 12,
                      fontStyle: 'italic', padding: '20px 0', textAlign: 'center',
                      border: `1px dashed ${border}`,
                    }}>
                      No nearby buildings found. Try increasing the radius, or check that the focal
                      building's Location field has a complete address that can be geocoded.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
